import { IonHeader, IonIcon, IonPage, IonTextarea, useIonRouter } from "@ionic/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  calendarOutline,
  chevronForwardOutline,
  clipboardOutline,
  closeOutline,
  copyOutline,
  documentTextOutline,
  flagOutline,
  hourglassOutline,
  imageOutline,
  locationOutline,
  logoWhatsapp,
  navigateOutline,
  openOutline,
  shareOutline,
} from "ionicons/icons";
import { toast } from "sonner";
import { LinkedText } from "@/components/atoms/linked-text";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { EmptyState } from "@/components/molecules/empty-state";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { BriefingText } from "@/components/molecules/shift/briefing-text";
import { DetailCard } from "@/components/molecules/shift/detail-card";
import { ShiftSettlement } from "@/components/molecules/shift/shift-settlement";
import { Toolbar } from "@/components/toolbar";
import { formatFullAddress } from "@/lib/address";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { useSelfCancellationAllowed } from "@/lib/backend/utils/hooks/use-self-cancellation";
import { Share } from "@/lib/capacitor/share";
import { isImageFile, splitFileName } from "@/lib/file-name";
import { queryOptions_shift_single } from "@/lib/providers/query/functions/shift/single";
import { Routes } from "@/lib/router";
import { venueBriefingToShow } from "@/lib/shift/venue-briefing";
import { openFile } from "@/lib/utils/download";
import { openNativeNavigation } from "@/lib/utils/navigation";
import { openExternalUrl } from "@/lib/utils/open-url";
import { logger } from "@/logger";
import { getAdditionalFieldIcon } from "@/utils/additional-field-icons";
import { formatInTz, formatTime } from "@/utils/format-datetime";

// Relative, like every other asset import in this repo. `assets/` sits at the project root,
// not under `src/`, and the `@/` alias does not cover it — Rollup fails to resolve it.

/**
 * The shift screen.
 *
 * Everything an employee wants to know about one shift used to be crammed into a list card plus a
 * hand-rolled popover, and tapping that card opened the _project_. This is the screen that was
 * missing: the shift itself, with room for all of its documents (not just the first one), the group
 * chat link, and the two things you might need to do about it — offer it to someone else, or ask to
 * be taken off it.
 *
 * Clocking in and submitting hours deliberately stay on the card: those are the one primary action
 * you want without opening anything.
 */
export function ShiftDetailPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const router = useIonRouter();
  const { id } = Routes.ShiftDetail.useParams();

  const [cancellationReason, setCancellationReason] = useState("");
  const modalOfferShift = useRef<HTMLIonModalElement>(null);
  const modalTransferRequest = useRef<HTMLIonModalElement>(null);
  const modalRequestCancellation = useRef<HTMLIonModalElement>(null);

  const { data: featureFlags } = useFeatureFlags();
  const shiftSwapEnabled = featureFlags?.shiftSwapEnabled ?? false;
  const selfCancellationAllowed = useSelfCancellationAllowed();

  const { data: shift, isLoading } = useQuery({
    ...queryOptions_shift_single(id ?? ""),
    enabled: !!id,
  });

  const projectId = shift?.projectId ?? undefined;

  // Two screens in one, split on whether the night is over. Offering a shift to a colleague or
  // asking to be taken off it are meaningless once it is worked; what matters then is your hours
  // and your claim. Read once per render rather than from a ticking clock — a shift that ends
  // while you have the screen open flips on the next visit, which is soon enough for this.
  const isFinished = !!shift?.endAt && new Date(shift.endAt).getTime() < Date.now();

  // Where "back" goes when the screen was opened cold, from a push or a pasted link: the list
  // this shift belongs to. Before it is known, the agenda is the safer guess — that is where the
  // large majority of shifts are.
  const backHref = isFinished ? Routes.CompletedShifts() : Routes.UpcomingShifts();

  // What the project says about itself. Both live on the project screen and neither was on the
  // shift — so the briefing, the dress code and "waar meld je je" were a screen away from the
  // thing they describe.
  const { data: additionalFields } = useQuery({
    queryKey: ["additionalFields", projectId],
    queryFn: async () => (await api.additionalField.getAll(projectId!)).data,
    enabled: !!projectId,
  });

  const { data: documents } = useQuery({
    queryKey: ["documents", projectId],
    queryFn: () => extractionWrapper(api.document.getAll("project", projectId!)),
    enabled: !!projectId,
  });

  const transferMutation = useMutation({
    mutationFn: async () => {
      const response = await api.shiftCancellation.createTransferRequest({
        shiftId: shift!.id,
        reason: t("shift:actions.offer-shift.action"),
      });
      return response.data;
    },
    onSuccess: async () => {
      toast.success(t("shift:actions.offer-shift.success"), {
        id: "offer-shift",
        duration: 2000,
      });
      modalOfferShift.current?.dismiss();
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["shift", id] }),
        queryClient.refetchQueries({ queryKey: ["shifts"] }),
      ]);
      setTimeout(() => modalTransferRequest.current?.present(), 100);
    },
    onError: (error: any) => {
      const errorCode =
        error?.error?.errorCode || error?.data?.errorCode || error?.errorCode || null;
      const errorMessage = errorCode ? t(`error-codes:${errorCode}`) : undefined;
      toast.error(errorMessage || t("shift:actions.offer-shift.error"), { id: "offer-shift" });
    },
  });

  const cancellationMutation = useMutation({
    mutationFn: async (reason?: string) => {
      const response = await api.shiftCancellation.requestCancellation({
        shiftId: shift!.id,
        reason: reason || t("shift:actions.request-cancellation.action"),
      });
      return response.data;
    },
    onSuccess: async () => {
      toast.success(t("shift:actions.request-cancellation.success"), {
        id: "request-cancellation",
      });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["shift", id] }),
        queryClient.invalidateQueries({ queryKey: ["shifts"] }),
      ]);
    },
    onError: (error: any) => {
      const errorCode =
        error?.error?.errorCode || error?.data?.errorCode || error?.errorCode || null;
      const errorMessage = errorCode ? t(`error-codes:${errorCode}`) : undefined;
      toast.error(errorMessage || t("shift:actions.request-cancellation.error"), {
        id: "request-cancellation",
      });
    },
  });

  const handleOpenDocument = async (name: string) => {
    if (!projectId) {
      return;
    }
    try {
      const response = await api.file.getProjectDocumentUrl(projectId, encodeURIComponent(name));
      openFile(response.data.url);
    } catch (error) {
      logger.error("Failed to open project document:", error);
      toast.error(t("shift:actions.briefing-error"));
    }
  };

  /**
   * The address as a human would write it, handed to whichever maps app the phone actually has. The
   * screen used to build a Google Maps _search_ URL by hand, which opens a browser tab on iOS and
   * searches for a street name without a city — two addresses in two towns look the same.
   */
  const handleNavigate = async () => {
    const project = shift?.project;
    const destination = formatFullAddress(project ?? {});

    if (!destination) {
      return;
    }

    try {
      await openNativeNavigation(destination, { label: project?.name, mode: "driving" });
    } catch (error) {
      logger.error("Failed to open navigation:", error);
      toast.error(t("shift:actions.route-error"));
    }
  };

  const handleCopyTransferLink = async () => {
    const transferUrl = shift?.stats?.transferRequestUrl;
    if (!transferUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(transferUrl);
      toast.success(t("shift:transfer.link-copied"));
    } catch (error) {
      logger.error("Clipboard write failed:", error);
      toast.error(t("shift:transfer.error"));
    }
  };

  const handleShareTransferLink = async () => {
    const transferUrl = shift?.stats?.transferRequestUrl;
    if (!transferUrl) {
      return;
    }
    try {
      if (await Share.canShare()) {
        await Share.share({
          title: shift?.project?.name || "Shift",
          text: t("shift:transfer.share-text"),
          url: transferUrl,
        });
      } else {
        toast.error(t("shift:transfer.share-unsupported"));
      }
    } catch (error) {
      logger.error("Share failed:", error);
    }
  };

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader>
          <Toolbar>
            <Buttons slot="secondary">
              <BackButton defaultHref={backHref} />
            </Buttons>
          </Toolbar>
        </IonHeader>
        <Content scrollUnderChrome>
          <div className="flex flex-col gap-4 py-6">
            <div className="h-40 w-full animate-pulse rounded-xl bg-surface-secondary" />
            <div className="h-6 w-2/3 animate-pulse rounded bg-surface-secondary" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-surface-secondary" />
          </div>
        </Content>
      </IonPage>
    );
  }

  // A shift can be gone — cancelled, reassigned, or a stale deep link from a push. Say so
  // instead of leaving a skeleton spinning forever, which is what a bare `|| !shift` did.
  if (!shift) {
    return (
      <IonPage>
        <IonHeader>
          <Toolbar>
            <Buttons slot="secondary">
              <BackButton defaultHref={backHref} />
            </Buttons>
          </Toolbar>
        </IonHeader>
        <Content scrollUnderChrome>
          <div className="flex flex-col items-center gap-4 py-10">
            <EmptyState
              icon={calendarOutline}
              title={t("routes:shift-detail.not-found.title")}
              description={t("routes:shift-detail.not-found.description")}
            />
            <button
              type="button"
              onClick={() => router.push(Routes.UpcomingShifts(), "back")}
              className="rounded-xl border-2 border-solid border-button-primary bg-button-primary px-5 py-3 text-md font-semibold leading-[1.5] text-txt-inverted"
            >
              {t("routes:shift-detail.not-found.cta")}
            </button>
          </div>
        </Content>
      </IonPage>
    );
  }

  // Alles op dit scherm in projecttijd, nooit in die van het toestel: een dienst van 15:00-23:00
  // las op een telefoon in Toronto als 09:00-17:00 en de dagkop viel een dag terug.
  const timeZone = shift.timeZone ?? shift.project?.timeZone;
  const addressLine = shift.project?.addressLine;
  const whatsappGroupLink = shift.project?.whatsappGroupLink;
  const hasPendingCancellation = !!shift.stats?.hasPendingCancellation;
  const hasPendingTransfer = !!shift.stats?.hasPendingTransfer;
  const hasFacts =
    !!shift.userFunction?.name ||
    !!shift.position?.name ||
    !!addressLine ||
    !!whatsappGroupLink ||
    hasPendingCancellation ||
    (shiftSwapEnabled && hasPendingTransfer);
  // De briefing die de planner op de locatie schreef, en die de opdracht alleen als kopie van bij
  // het aanmaken meekreeg. Alleen tonen als hij iets toevoegt; de regel staat in `venueBriefing`.
  const projectDescription = shift.project?.description ?? "";
  const venueDescription = venueBriefingToShow(
    projectDescription,
    shift.project?.venueBriefing?.description,
  );
  // A heading over an empty card is worse than no card. Requesting to be taken off the shift is
  // not counted: it sits outside the card, and stands on its own.
  const hasActions =
    !!projectId ||
    (!isFinished && shiftSwapEnabled && (hasPendingTransfer || !hasPendingCancellation));

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <Buttons slot="secondary">
            <BackButton defaultHref={backHref} />
          </Buttons>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome className="part-scroll:gap-6">
        {/* `shrink-0` is load-bearing. The scroll container is a flex column, and a box that gets
            its height from `aspect-ratio` while clipping its overflow has an automatic minimum size
            of zero — so the moment the page is taller than the screen, flex takes the whole overflow
            out of the image and it disappears without a trace. */}
        <ProjectImage
          projectId={projectId}
          imageKey={shift.project?.image}
          alt={shift.project?.name || "Project"}
          className="aspect-video w-full shrink-0 rounded-xl"
        />

        {/* When and what, in the title itself. The time used to be the first row of a list where it
            sat next to the address and the dress code — the one fact you open this screen for,
            given the same weight as everything else. */}
        <header className="flex flex-col gap-1">
          <p className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
            {t(isFinished ? "routes:shift-detail.eyebrow-finished" : "routes:shift-detail.eyebrow")}
          </p>
          <h1 className="text-2xl font-semibold leading-[1.2] text-txt-primary">
            {shift.project?.name}
          </h1>
          <p className="text-md leading-[1.5] text-txt-secondary">
            <span className="capitalize">
              {formatInTz(shift.startAt, {
                weekday: "long",
                day: "numeric",
                month: "long",
                timeZone,
              })}
            </span>
            <span className="px-1.5 text-txt-tertiary">·</span>
            <span className="font-semibold text-txt-primary">
              {formatTime(shift.startAt, { timeZone })} – {formatTime(shift.endAt, { timeZone })}
            </span>
          </p>
        </header>

        {/* What you need to turn up: your role, your spot, the address. One block, so the rest of
            the screen reads as background rather than competing with it. */}
        {hasFacts && (
          <DetailCard>
            {shift.userFunction?.name && (
              <FactRow
                icon={clipboardOutline}
                label={t("routes:shift-detail.function")}
                value={shift.userFunction.name}
              />
            )}
            {shift.position?.name && (
              <FactRow
                icon={flagOutline}
                label={t("routes:shift-detail.position")}
                value={shift.position.name}
              />
            )}
            {addressLine && (
              <FactRow
                icon={locationOutline}
                label={t("common:location.title")}
                value={addressLine}
                onClick={handleNavigate}
                action={t("routes:shift-detail.route")}
                actionIcon={navigateOutline}
                actionLabel={t("shift:actions.view-route")}
              />
            )}
            {/* De groepschat hoort bij het rijtje "wat moet ik weten om te komen werken", niet
                bij de acties onderaan: het is een feit over deze dienst, net als het adres. De
                uitnodigingslink zelf staat er niet als waarde. Die is lang, wordt afgekapt en
                zegt een medewerker niets; de rij zegt welke chat het is en de pil opent hem. */}
            {whatsappGroupLink && (
              <FactRow
                icon={logoWhatsapp}
                label={t("routes:shift-detail.group-chat")}
                value={t("routes:shift-detail.whatsapp-group")}
                onClick={() => openExternalUrl(whatsappGroupLink)}
                action={t("routes:shift-detail.open")}
                actionIcon={openOutline}
                actionLabel={t("shift:actions.whatsapp-group")}
              />
            )}
            {hasPendingCancellation && (
              <FactRow
                icon={hourglassOutline}
                value={t("shift:status.cancellation-pending")}
                tone="warning"
              />
            )}
            {shiftSwapEnabled && hasPendingTransfer && !hasPendingCancellation && (
              <FactRow
                icon={shareOutline}
                value={t("shift:status.transfer-pending")}
                tone="warning"
              />
            )}
          </DetailCard>
        )}

        {/* The briefing, under one heading. The description and the extra fields are both the
            planner telling you about this project; two headings above two paragraphs made the
            screen read like a form.

            De locatietekst is de uitzondering en krijgt wel een eigen kopje: die gaat niet over
            deze dienst maar over de plek, en staat er vaak al jaren, geschreven voor iedereen die
            er komt werken. Zonder kopje lees je hem als een vervolg op de opdrachttekst. */}
        {(projectDescription || venueDescription || !!additionalFields?.length) && (
          <section className="flex flex-col gap-3">
            <h2 className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
              {t("routes:shift-detail.about")}
            </h2>

            {projectDescription && <BriefingText text={projectDescription} />}

            {venueDescription && (
              <div className="flex w-full flex-col items-start gap-1">
                {/* Eén stap stiller dan het sectiekopje erboven: semibold in plaats van bold, en
                    tertiair in plaats van secundair. Anders domineert het subkopje zijn kop. */}
                <h3 className="text-xs font-semibold uppercase tracking-wide text-txt-tertiary">
                  {t("routes:shift-detail.about-venue")}
                </h3>
                <BriefingText text={venueDescription} />
              </div>
            )}

            {!!additionalFields?.length && (
              <DetailCard>
                {additionalFields.map((field) => {
                  const FieldIcon = getAdditionalFieldIcon(field.icon);
                  return (
                    <div key={field.id} className="flex items-start gap-3 py-3.5">
                      <FieldIcon
                        className="mt-0.5 size-5 shrink-0 text-txt-tertiary"
                        aria-hidden="true"
                      />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-md font-medium leading-[1.5] text-txt-primary">
                          {field.title}
                        </span>
                        {field.description && (
                          <span className="whitespace-pre-wrap text-sm leading-[1.4] text-txt-secondary">
                            <LinkedText text={field.description} />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </DetailCard>
            )}
          </section>
        )}

        {/* Every document, not just documents[0] — the old briefing button opened the first
            one it found, which is a guess as soon as a project has two. */}
        {!!documents?.items?.length && (
          <DetailCard title={t("routes:shift-detail.documents")}>
            {documents.items.map((document) => (
              <DocumentRow
                key={document.name}
                name={document.name}
                onClick={() => handleOpenDocument(document.name)}
              />
            ))}
          </DetailCard>
        )}

        {isFinished && <ShiftSettlement shift={shift} />}

        <div className="flex flex-col gap-3">
          {hasActions && (
            <DetailCard title={t("routes:shift-detail.actions")}>
              {projectId && (
                <ActionRow
                  icon={calendarOutline}
                  label={t("shift:actions.view-availability")}
                  onClick={() =>
                    router.push(
                      Routes.Project({ id: projectId }, { search: { origin: "upcoming" } }),
                      "forward",
                    )
                  }
                />
              )}

              {/* Nothing below this line applies to a shift that has already been worked: you
                  cannot hand over last Saturday, and asking to be taken off it is not a thing. */}
              {!isFinished && shiftSwapEnabled && hasPendingTransfer && (
                <ActionRow
                  icon={shareOutline}
                  label={t("shift:transfer.view")}
                  onClick={() => modalTransferRequest.current?.present()}
                />
              )}

              {!isFinished &&
                shiftSwapEnabled &&
                !hasPendingTransfer &&
                !hasPendingCancellation && (
                  <ActionRow
                    icon={shareOutline}
                    label={t("shift:actions.offer-shift.action")}
                    onClick={() => modalOfferShift.current?.present()}
                  />
                )}
            </DetailCard>
          )}

          {/* Out of the card on purpose. Asking to be taken off a shift is a last resort, not the
              fifth item on a menu — it kept the same weight as opening the group chat. */}
          {/* Staat de organisatie geen zelfbediening toe, dan komt op dezelfde plek een regel
              uitleg in plaats van de knop. Een knop die op een 400 eindigt is kapot, maar een knop
              die er gisteren was en vandaag niet meer leest als een app die iets kwijt is: de
              medewerker gaat zoeken en vindt niets. Eén zin zegt dat het niet aan hem ligt en wie
              het wél kan — dat hij de planner belt is precies de bedoeling van die instelling. */}
          {!isFinished &&
            !hasPendingCancellation &&
            (selfCancellationAllowed ? (
              <button
                type="button"
                onClick={() => modalRequestCancellation.current?.present()}
                className="touch-target self-start text-md font-medium text-error-120 transition-opacity active:opacity-70"
              >
                {t("shift:actions.request-cancellation.action")}
              </button>
            ) : (
              <p className="self-start text-sm leading-relaxed text-txt-secondary">
                {t("shift:actions.request-cancellation.not-allowed")}
              </p>
            ))}
        </div>
      </Content>

      {shiftSwapEnabled && (
        <ModalSheetAutoHeight ref={modalOfferShift}>
          <div className="flex flex-col gap-6 p-6">
            <h2 className="text-xl font-bold text-txt-primary">
              {t("shift:actions.offer-shift.confirm.title")}
            </h2>
            <p className="text-sm leading-relaxed text-txt-secondary">
              {t("shift:actions.offer-shift.confirm.description")}
            </p>
            <button
              type="button"
              onClick={() => transferMutation.mutate()}
              disabled={transferMutation.isPending}
              className="w-full rounded-lg border border-solid border-bordercolor-primary bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:opacity-50"
            >
              {transferMutation.isPending
                ? t("shift:actions.offer-shift.loading")
                : t("shift:actions.offer-shift.confirm.button")}
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}

      {shiftSwapEnabled && (
        <ModalSheetAutoHeight ref={modalTransferRequest}>
          <div className="flex flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-txt-primary">{t("shift:transfer.title")}</h2>
              <button
                type="button"
                onClick={() => modalTransferRequest.current?.dismiss()}
                className="flex size-8 items-center justify-center"
                aria-label={t("common:close")}
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-tertiary" />
              </button>
            </div>

            <div className="flex flex-col items-center gap-1 text-center">
              <h3 className="text-xl font-bold text-txt-primary">{shift.project?.name}</h3>
              <p className="text-md text-txt-secondary">
                {formatInTz(shift.startAt, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  timeZone,
                })}
                , {formatTime(shift.startAt, { timeZone })} –{" "}
                {formatTime(shift.endAt, { timeZone })}
              </p>
              {shift.userFunction?.name && (
                <p className="text-md font-medium text-brand-500">{shift.userFunction.name}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCopyTransferLink}
                className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-solid border-bordercolor-primary px-4 py-4 transition-colors active:bg-surface-secondary"
              >
                <IonIcon icon={copyOutline} className="text-2xl text-txt-primary" />
                <span className="text-sm font-medium text-txt-primary">
                  {t("shift:transfer.copy-link")}
                </span>
              </button>
              <button
                type="button"
                onClick={handleShareTransferLink}
                className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-solid border-bordercolor-primary px-4 py-4 transition-colors active:bg-surface-secondary"
              >
                <IonIcon icon={shareOutline} className="text-2xl text-txt-primary" />
                <span className="text-sm font-medium text-txt-primary">
                  {t("shift:transfer.share")}
                </span>
              </button>
            </div>
          </div>
        </ModalSheetAutoHeight>
      )}

      <ModalSheetAutoHeight
        ref={modalRequestCancellation}
        onDidDismiss={() => setCancellationReason("")}
      >
        <div className="flex flex-col gap-6 p-6">
          <h2 className="text-xl font-bold text-txt-primary">
            {t("shift:actions.request-cancellation.confirm.title")}
          </h2>
          <p className="text-sm leading-relaxed text-txt-secondary">
            {t("shift:actions.request-cancellation.confirm.description")}
          </p>
          <IonTextarea
            label={t("shift:actions.request-cancellation.reason.label")}
            placeholder={t("shift:actions.request-cancellation.reason.placeholder")}
            value={cancellationReason}
            onIonInput={(e) => setCancellationReason(e.detail.value || "")}
            rows={4}
            labelPlacement="stacked"
            className="w-full"
          />
          <button
            type="button"
            onClick={async () => {
              await cancellationMutation.mutateAsync(cancellationReason.trim() || undefined);
              setCancellationReason("");
              modalRequestCancellation.current?.dismiss();
            }}
            disabled={cancellationMutation.isPending}
            className="w-full rounded-lg border border-solid border-bordercolor-primary bg-error-100 py-3 text-center text-base font-semibold text-white disabled:opacity-50"
          >
            {cancellationMutation.isPending
              ? t("shift:actions.request-cancellation.loading")
              : t("shift:actions.request-cancellation.confirm.button")}
          </button>
        </div>
      </ModalSheetAutoHeight>
    </IonPage>
  );
}

/**
 * One fact about this shift: what it is, and what it says.
 *
 * The label goes above the value rather than under it. Reading a column of these, the labels are
 * what you skip and the values are what you came for — so the values carry the weight, and the
 * labels are the small print that tells you which is which.
 */
function FactRow({
  icon,
  label,
  value,
  tone = "default",
  onClick,
  action,
  actionIcon,
  actionLabel,
}: {
  icon: string;
  /** Omitted for a status line, which is a sentence and not a labelled value. */
  label?: string;
  value: string;
  tone?: "default" | "warning";
  onClick?: () => void;
  /** Text of the trailing pill; makes the row tappable together with `onClick`. */
  action?: string;
  actionIcon?: string;
  actionLabel?: string;
}) {
  const isWarning = tone === "warning";

  const body = (
    <>
      <IonIcon
        icon={icon}
        className={`mt-0.5 size-5 shrink-0 ${isWarning ? "text-warning-120" : "text-txt-tertiary"}`}
        aria-hidden="true"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {label && <span className="text-sm leading-[1.4] text-txt-secondary">{label}</span>}
        <span
          className={`text-md font-semibold leading-[1.5] ${isWarning ? "text-warning-120" : "text-txt-primary"}`}
        >
          {value}
        </span>
      </div>
      {action && (
        <span className="flex shrink-0 items-center gap-1.5 self-center rounded-full bg-surface-background px-3 py-1.5">
          {actionIcon && <IonIcon icon={actionIcon} className="size-4 text-txt-brand" />}
          <span className="text-13 font-semibold leading-[1.4] text-txt-brand">{action}</span>
        </span>
      )}
    </>
  );

  if (!onClick) {
    return <div className="flex items-start gap-3 py-3.5">{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={actionLabel}
      className="flex w-full items-start gap-3 py-3.5 text-left transition-opacity active:opacity-70"
    >
      {body}
    </button>
  );
}

/**
 * A row that does something. These used to live in a hand-positioned `div` floating over the card,
 * which clipped inside scroll containers and ignored safe areas; on a screen they are just rows.
 */
/**
 * A row that does something.
 *
 * Icon on the left, in the brand colour, and a chevron on the right. It used to be the other way
 * around: the WhatsApp logo sat where a chevron goes, which reads as "this is where you tap" and
 * left the row itself looking like a label.
 */
function ActionRow({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 py-4 text-left transition-opacity active:opacity-70"
    >
      <IonIcon icon={icon} className="size-5 shrink-0 text-txt-brand" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-md font-medium text-txt-primary">{label}</span>
      <IonIcon
        icon={chevronForwardOutline}
        className="size-4 shrink-0 text-txt-tertiary"
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * A project document.
 *
 * The file name is the only thing there is to go on, and it is usually written for a filing system
 * rather than for a phone: "2025 - RBI25 - Legal Aid T&C - NL.pdf". So the extension comes off and
 * becomes a label, and the name gets two lines to wrap in instead of being cut off mid-word.
 */
function DocumentRow({ name, onClick }: { name: string; onClick: () => void }) {
  const { base, extension } = splitFileName(name);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 py-3.5 text-left transition-opacity active:opacity-70"
    >
      <IonIcon
        icon={isImageFile(extension) ? imageOutline : documentTextOutline}
        className="size-5 shrink-0 text-txt-brand"
        aria-hidden="true"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="line-clamp-2 text-md font-medium leading-[1.4] text-txt-primary">
          {base}
        </span>
        {extension && (
          <span className="text-sm uppercase leading-[1.4] text-txt-secondary">{extension}</span>
        )}
      </div>
      <IonIcon
        icon={openOutline}
        className="size-4 shrink-0 text-txt-tertiary"
        aria-hidden="true"
      />
    </button>
  );
}
